<template>
  <div>
    <TabbedDataTable class="mb-4" :tabs="tabs" @careDocumentation="openSoinsDocumentationModal" @addItem="addItem" @exportItems="exportItems" @editItem="editItem" @deleteItem="deleteItem" />
    <SessionCreate  />
    <SessionDetailPopIn />
    <ConfirmationModal title="Supprimer la session" :activator.sync="confirmationModal" :close-modal="closeConfirmationModal" :action="deleteSession" :loading="loading">
      <template #body>
        <h3 class="black--text">
          Êtes-vous sûr de vouloir supprimer la session?
          Une fois supprimée, cette action ne peut pas être annulée et toutes les ressources associées seront également supprimées des sessions
        </h3>
      </template>
    </ConfirmationModal>
    <SoinsDocumentationModal v-if="documentationModal" :show="documentationModal" :documentation-item="documentationItem" @closeModal="closeSoinsDocumentationModal" />
  </div>
</template>

<script>
import TabbedDataTable from '../../common/TabbedDataTable.vue';
import SessionCreate from '../../sessions/SessionCreate.vue';
import SessionDetailPopIn from '../../sessions/SessionDetailPopIn.vue';
import ConfirmationModal from '../../common/ConfirmationModal.vue';
import SoinsDocumentationModal from '@/components/resourcePage/Horse/SoinsDocumentationModal.vue';

import { ref, computed } from '@vue/composition-api';
import { format } from 'date-fns';
import { filteredSessionPerContentType } from '../../../helpers/dataManager';
import { EventBus, downloadCSV, sendNotification } from '../../../helpers/utils';
import { isBefore } from 'date-fns';
import { handleDeleteSession } from '../../../helpers/sessions';
import store from '@/store';
import { insertContentTypeSession, updateContentTypeSession, deleteContentTypeSession } from '../../../helpers/amplify';

const localReferences = require('@/configs/references.json');

export default {
  components: { TabbedDataTable, SessionCreate, SessionDetailPopIn, ConfirmationModal, SoinsDocumentationModal },
  props: {
    horse: {
      type: Object,
      default () {
        return {};
      }
    }
  },
  setup (props) {
    const selectedDate = computed(() => store.getters.selectedDate);
    const loading = ref(false);
    const confirmationModal = ref(false);
    const sessionToDelete = ref(null);

    const documentationModal = ref(false);
    const documentationItem = ref(null);

    const headers = ref([
      { text: 'Date', value: 'date', align: 'center', sortable: true },
      { text: 'Date à suivre', value: 'nextCareDate', align: 'center', sortable: true },
      { text: 'Type de soin', value: 'careType', align: 'center', sortable: true },
      { text: 'Observation', value: 'observation', align: 'center', sortable: true },
      { text: '', value: 'actions', align: 'center', sortable: false }
    ]);

    const computeCares = (session) => {
      return {
        id: session.id,
        uuid: session.uuid,
        date: format(new Date(session.start_time * 1000), 'yyyy/MM/dd'),
        nextCareDate: '--',
        careType: localReferences.horse_treatment_type[session.treatment_type] || localReferences.legacy_horse_treatment_type[session.treatment_type] || '--',
        observation: session.observation || '--'
      };
    };

    const horseCares = computed(() => {
      const horseSoins = filteredSessionPerContentType(props.horse, 'type', ['SOINS']);
      const pastCares = [];
      const futureCares = [];
      horseSoins.forEach(session => {
        if (isBefore(new Date(session.start_time * 1000), new Date())) {
          pastCares.push(computeCares(session));
        } else {
          futureCares.push(computeCares(session));
        }
      });
      return { pastCares, futureCares };
    });

    const tabs = computed(() => [
      {
        index: 0,
        name: 'Historique',
        headerLabel: 'Historique des soins',
        headerButtons: [
          {
            plain: true,
            iconName: 'mdi-plus',
            emitName: 'addItem'
          },
          {
            plain: true,
            iconName: 'mdi-export',
            emitName: 'exportItems'
          }
        ],
        tableHeaders: headers.value,
        tableData: horseCares.value.pastCares,
        tableHeight: '350px',
        itemActions: [
          {
            color: 'black',
            plain: true,
            iconName: 'mdi-pencil',
            emitName: 'editItem',
          },
          {
            color: 'black',
            plain: true,
            iconName: 'mdi-delete',
            emitName: 'deleteItem',
          },
          {
            color: 'black',
            plain: true,
            iconName: 'mdi-file-upload-outline',
            emitName: 'careDocumentation',
          }
        ],
        itemsPerPage: 10,
        sortBy: 'date',
        sortDesc: true,
      },
      {
        index: 1,
        name: 'Planifiés',
        headerLabel: 'Soins Planifiés',
        headerButtons: [
          {
            plain: true,
            iconName: 'mdi-plus',
            emitName: 'addItem'
          },
          {
            plain: true,
            iconName: 'mdi-export',
            emitName: 'exportItems'
          }
        ],
        tableHeaders: headers.value,
        tableData: horseCares.value.futureCares,
        tableHeight: '350px',
        itemActions: [
          {
            color: 'black',
            plain: true,
            iconName: 'mdi-pencil',
            emitName: 'editItem',
          },
          {
            color: 'black',
            plain: true,
            iconName: 'mdi-delete',
            emitName: 'deleteItem',
          }
        ],
        itemsPerPage: 10,
        sortBy: 'date',
        sortDesc: true,
      }
    ]);

    const addItem = () => {
      const startTime = new Date(`${selectedDate.value}T${format(new Date(), 'HH:mm')}`);
      const minutes = Math.round(startTime.getMinutes() / 15) * 15;
      startTime.setMinutes(minutes);
      EventBus.$emit('showSessionCreate', { time: format(startTime, 'HH:mm'), typeSession: {type: 'SOINS', mandatory: true}, selectSessionDate: true, title: 'Création de soin', horses: [{ uuid: props.horse.uuid, wtitle: props.horse.wtitle }] });
    };

    const exportItems = ({tabIndex}) => {
      downloadCSV([{ headers: headers.value, data: tabs.value[tabIndex].tableData || [] }], tabs.value[tabIndex].headerLabel);
    };

    const editItem = ({item}) => {
      const { contentTypeAllSessions } = store.state;
      const session = contentTypeAllSessions.find(session => session.id === item.id);

      EventBus.$emit('showEditSession', { session: session }, { selectSessionDate: true })
    };

    const deleteItem = ({item}) => {
      sessionToDelete.value = item;
      confirmationModal.value = true;
    };

    const closeConfirmationModal = () => {
      confirmationModal.value = false;
      sessionToDelete.value = null;
    };

    const deleteSession = async () => {
      loading.value = true;
      const response = await handleDeleteSession('SINGLE', sessionToDelete.value);
      sendNotification(response.success ? 'success' : 'error', response.message);

      if (response.success) {
        deleteContentTypeSession(sessionToDelete.value);
      }
      loading.value = false;
      closeConfirmationModal();
    };

    const openSoinsDocumentationModal = ({item}) => {
      documentationItem.value = { 
        horse: { uuid: props.horse.uuid, name: props.horse.wtitle },
        session: { id: item.id, date: format(new Date(item.date), 'dd/MM/yyyy'), careType: item.careType }
      };
      documentationModal.value = true;
    };

    const closeSoinsDocumentationModal = () => {
      documentationModal.value = false;
    };

    EventBus.$on('createdSessions', (sessions) => {
      (sessions || []).forEach(session => {
        insertContentTypeSession(session);
      });
    });

    EventBus.$on('editedSessions', (sessions) => {
      (sessions || []).forEach(session => {
        updateContentTypeSession(session);
      });
    });

    EventBus.$on('deletedSessions', (sessions) => {
      (sessions || []).forEach(session => {
        deleteContentTypeSession(session);
      });
    });
    


    return { headers, horseCares, tabs, addItem, exportItems, editItem, deleteItem, deleteSession, confirmationModal, closeConfirmationModal, deleteSession, loading, openSoinsDocumentationModal, closeSoinsDocumentationModal, documentationModal, documentationItem };
  }
};
</script>